import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';

const AIAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message on mount
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: 'Hello! I\'m your TELUS Sales Assistant. I can help you with questions about sales processes, pricing, objection handling, and more. What would you like to know?',
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    // Add user message to chat
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/ai-assistant/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ question: userMessage.content })
      });

      const data = await response.json();

      if (data.success) {
        // Add AI response to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.answer,
          timestamp: data.timestamp
        }]);
      } else {
        setError(data.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('AI Assistant error:', err);
      setError('Failed to connect to AI Assistant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([{
      role: 'assistant',
      content: 'Starting a new conversation. How can I help you with TELUS sales today?',
      timestamp: new Date().toISOString()
    }]);
    setError(null);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon color="primary" />
            <Typography variant="h6">TELUS Sales AI Assistant</Typography>
            <Chip
              label="Session-based (no history saved)"
              size="small"
              variant="outlined"
            />
          </Box>
          <IconButton onClick={handleNewChat} title="New Chat">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Messages Area */}
      <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {messages.map((message, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {message.role === 'assistant' ? (
                  <SmartToyIcon color="primary" sx={{ mt: 0.5 }} />
                ) : (
                  <PersonIcon color="action" sx={{ mt: 0.5 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {message.role === 'assistant' ? 'AI Assistant' : 'You'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: message.role === 'assistant' ? 'grey.50' : 'primary.50',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: message.role === 'assistant' ? 600 : 400 }}>
                      {message.content}
                    </Typography>


                  </Paper>
                </Box>
              </Box>
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                AI is thinking...
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <Divider />

        {/* Input Area */}
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about TELUS sales, pricing, objection handling..."
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white'
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark'
              },
              '&:disabled': {
                backgroundColor: 'grey.300'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Example Questions */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Example questions you can ask:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            'How do I handle Shaw contract objections?',
            'What is the pricing for Internet 1000?',
            'How does fiber differ from copper?',
            'What is the assumed close technique?',
            'How to calculate TV package pricing?'
          ].map((question, index) => (
            <Chip
              key={index}
              label={question}
              size="small"
              onClick={() => setInput(question)}
              clickable
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default AIAssistant;